import React, { useEffect, useMemo, useState } from 'react'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { DatePickerCustomDropDown } from '../../DatePickerCustom'
import ChatStatsClassPicker from './ChatStatsClassPicker'

export const CHAT_TYPES = [
    { value: 'teacher', labelKey: 'chatStatsTypeTeacher', color: '#fae480', ink: '#2d2b45' },
    { value: 'counselor', labelKey: 'chatStatsTypeCounsellor', color: '#f47c39', ink: '#ffffff' },
    { value: 'sos', labelKey: 'chatStatsTypeSos', color: '#689cfa', ink: '#ffffff' },
]
export const ALL_TYPES = CHAT_TYPES.map((type) => type.value)

const isoDay = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
const daysAgo = (count) => {
    const date = new Date()
    date.setDate(date.getDate() - count)
    return isoDay(date)
}

const PRESETS = [
    { key: '7d', labelKey: 'chatStatsRange7Days', range: () => ({ start_date: daysAgo(6), end_date: isoDay(new Date()) }) },
    { key: '30d', labelKey: 'chatStatsRange30Days', range: () => ({ start_date: daysAgo(29), end_date: isoDay(new Date()) }) },
    { key: 'year', labelKey: 'chatStatsRangeThisYear', range: () => ({ start_date: `${new Date().getFullYear()}-01-01`, end_date: isoDay(new Date()) }) },
    { key: 'custom', labelKey: 'chatStatsRangeCustom', range: null },
]

const pill = (active) => ({
    padding: '9px 16px',
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    border: active ? '1px solid transparent' : '1px solid #e4e3f0',
    background: active ? '#5e639b' : '#ffffff',
    color: active ? '#ffffff' : '#8e8fb4',
})

function CustomRangeButton({ active, filters, onPresetChange }) {
    const { t } = useTranslation()
    const [pickerOpen, setPickerOpen] = useState(false)
    const [draftStart, setDraftStart] = useState(null)
    const [draftEnd, setDraftEnd] = useState(null)

    useEffect(() => {
        if (pickerOpen) {
            setDraftStart(filters.start_date ? new Date(filters.start_date) : null)
            setDraftEnd(filters.end_date ? new Date(filters.end_date) : null)
        }
    }, [pickerOpen, filters.start_date, filters.end_date])

    const handleDateSelect = (date) => {
        if (!draftStart || (draftStart && draftEnd)) {
            setDraftStart(date)
            setDraftEnd(null)
            return
        }
        if (date > draftStart) setDraftEnd(date)
        else {
            setDraftStart(date)
            setDraftEnd(null)
        }
    }

    const handleOkButton = () => {
        if (!draftStart || !draftEnd) return
        onPresetChange({ key: 'custom', range: () => ({ start_date: isoDay(draftStart), end_date: isoDay(draftEnd) }) })
        setPickerOpen(false)
    }

    const label = useMemo(() => {
        if (!active || !filters.start_date || !filters.end_date) return t('chatStatsRangeCustom')
        return `${format(new Date(filters.start_date), 'dd.MM.yyyy.')} - ${format(new Date(filters.end_date), 'dd.MM.yyyy.')}`
    }, [active, filters.start_date, filters.end_date, t])

    return (
        <div style={{ position: 'relative', zIndex: 3 }} onMouseLeave={() => setPickerOpen(false)}>
            <button type="button" style={pill(active || pickerOpen)} onClick={() => setPickerOpen((prev) => !prev)}>
                {label}
            </button>
            {pickerOpen && (
                <DatePickerCustomDropDown
                    showPredefinedDates={false}
                    startDate={draftStart}
                    endDate={draftEnd}
                    handleDateSelect={handleDateSelect}
                    handleOkButton={handleOkButton}
                    closePicker={() => setPickerOpen(false)}
                />
            )}
        </div>
    )
}

function ChatStatsFilters({ filters, preset, classes, onPresetChange, onChange }) {
    const { t } = useTranslation()
    const activeTypes = filters.types.length ? filters.types : ALL_TYPES

    const toggleType = (value) => {
        const next = activeTypes.includes(value) ? activeTypes.filter((item) => item !== value) : [...activeTypes, value]
        if (!next.length) return
        onChange({ ...filters, types: next.length === ALL_TYPES.length ? [] : next })
    }

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 22 }}>
            {PRESETS.map((item) =>
                item.key === 'custom' ? (
                    <CustomRangeButton key={item.key} active={preset === 'custom'} filters={filters} onPresetChange={onPresetChange} />
                ) : (
                    <button key={item.key} type="button" style={pill(preset === item.key)} onClick={() => onPresetChange(item)}>
                        {t(item.labelKey)}
                    </button>
                )
            )}

            <ChatStatsClassPicker classes={classes} value={filters.class_ids[0] || ''} onChange={(classId) => onChange({ ...filters, class_ids: classId ? [classId] : [] })} />

            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                <span style={{ fontSize: 12, color: '#8e8fb4', fontWeight: 600 }}>{t('chatStatsTypeLabel')}</span>
                {CHAT_TYPES.map((type) => {
                    const active = activeTypes.includes(type.value)
                    return (
                        <button
                            key={type.value}
                            type="button"
                            onClick={() => toggleType(type.value)}
                            style={{
                                padding: '9px 13px',
                                borderRadius: 999,
                                fontSize: 13,
                                fontWeight: 600,
                                cursor: 'pointer',
                                border: active ? '1px solid transparent' : '1px solid #e4e3f0',
                                background: active ? type.color : '#ffffff',
                                color: active ? type.ink : '#8e8fb4',
                            }}
                        >
                            {t(type.labelKey)}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

export default ChatStatsFilters
